/**
 * Types du domaine. Aucun import : ce fichier est la racine de la hierarchie et
 * il est partage par la couche pure (`analysis/`), la base (`db/`) et l'UI.
 */

/** Instant precis, stocke en ISO 8601 **UTC** (suffixe `Z`).
 *
 * L'UTC n'est pas un detail : il rend l'ordre lexicographique identique a
 * l'ordre chronologique, ce dont dependent a la fois les index Dexie et le
 * decoupage des nuits, qui parcourt les evenements tries.
 */
export type Instant = string

/** Jour calendaire **local**, au format `YYYY-MM-DD`. */
export type DayKey = string

export type Id = string

// ---------------------------------------------------------------------------
// Evenements bruts
// ---------------------------------------------------------------------------

/**
 * Faits remontes par les automatisations Raccourcis d'iOS. **Aucune
 * interpretation a ce niveau** : un `branche` ne dit pas « coucher », il dit
 * « le chargeur a ete connecte ». Toute la lecture se fait dans
 * `analysis/nuits.ts`, ce qui permet de changer les regles et de rejouer
 * l'historique sans jamais retoucher l'iPhone.
 *
 *   'ouverture'  O  une app surveillee a ete ouverte
 *   'fermeture'  V  une app surveillee a ete fermee
 *   'branche'    P  chargeur connecte
 *   'debranche'  U  chargeur deconnecte
 *   'alarme'     A  alarme arretee
 *   'appli'      -  battement de la PWA elle-meme (repli, jamais un raccourci)
 */
export type TypeEvenement =
  | 'ouverture'
  | 'fermeture'
  | 'branche'
  | 'debranche'
  | 'alarme'
  | 'appli'

export const TYPES_EVENEMENT: TypeEvenement[] = [
  'ouverture',
  'fermeture',
  'branche',
  'debranche',
  'alarme',
  'appli',
]

export const TYPE_EVENEMENT_LABELS: Record<TypeEvenement, string> = {
  ouverture: 'app ouverte',
  fermeture: 'app fermée',
  branche: 'chargeur branché',
  debranche: 'chargeur débranché',
  alarme: 'alarme arrêtée',
  appli: 'ouverture de l’app',
}

export interface Evenement {
  /** `${type}-${at}`. La deduplication est structurelle : reingerer deux fois
   *  le meme contenu ne peut pas creer de doublon, ce qui rend l'import manuel
   *  de `historique.txt` sans danger meme s'il recouvre des mois deja lus. */
  id: Id
  type: TypeEvenement
  at: Instant
  creeLe: Instant
}

// ---------------------------------------------------------------------------
// Nuits
// ---------------------------------------------------------------------------

/**
 * Usage du telephone au milieu d'une nuit.
 *
 * Se reveiller a 3 h, consulter son telephone, puis se rendormir est le cas
 * normal. Une nuit n'est donc pas un intervalle mais une suite de segments,
 * et c'est l'utilisateur qui tranche au reveil si l'interruption compte.
 */
export interface Interruption {
  debut: Instant
  fin: Instant
  /** `true` = comptee comme du repos malgre tout (coup d'oeil a l'heure). */
  ignoree: boolean
  /** `true` quand aucune `ouverture` n'a ete captee : on connait la fin de
   *  l'usage mais pas son debut, donc `debut === fin`. Mieux vaut afficher un
   *  reveil de duree inconnue que d'en inventer la longueur. */
  dureeInconnue: boolean
}

export interface Candidat {
  at: Instant
  type: TypeEvenement
}

export type SourceNuit = 'raccourci' | 'heuristique' | 'manuel'

export const SOURCE_LABELS: Record<SourceNuit, string> = {
  raccourci: 'raccourcis iOS',
  heuristique: 'estimée d’après l’usage de l’app',
  manuel: 'saisie à la main',
}

export interface Nuit {
  /** Cle primaire = jour du **lever**. Une nuit par matin, sans ambiguite. */
  date: DayKey
  debut: Instant
  fin: Instant
  interruptions: Interruption[]
  /** Tous les candidats retenus par les regles, y compris ceux non choisis :
   *  l'arbitrage doit pouvoir etre refait sans relire le journal d'evenements. */
  candidatsDebut: Candidat[]
  candidatsFin: Candidat[]
  origineDebut: TypeEvenement
  origineFin: TypeEvenement
  source: SourceNuit
  /** Une nuit corrigee n'est **jamais** recalculee : sans cette regle, la
   *  synchro du lendemain ecraserait l'arbitrage de la veille. */
  corrigee: boolean
  notes?: string
  modifieLe: Instant
}

/**
 * Matin ou des evenements existent mais ou aucune nuit plausible n'a pu etre
 * formee. On ne se tait pas : le silence ferait disparaitre la nuit sans que
 * rien ne le signale, alors qu'une anomalie affichee se corrige en deux gestes.
 */
export interface Anomalie {
  date: DayKey
  raison: 'duree-hors-bornes' | 'debut-manquant' | 'fin-manquante'
  message: string
  evenements: Evenement[]
}

// ---------------------------------------------------------------------------
// Reglages
// ---------------------------------------------------------------------------

export type PreferenceFin = 'premier' | 'alarme' | 'debranche'

export interface Reglages {
  /** Objectif de temps pose, en minutes. */
  objectifMinutes: number
  /** Deux candidats separes de plus de ce delai n'appartiennent pas a la meme
   *  soiree : au-dela, on est face a un reveil nocturne, pas a un coucher. */
  seuilNuitMinutes: number
  /** Deux evenements plus proches que ce delai appartiennent au meme usage. */
  seuilFusionMinutes: number
  /** En dessous de cette duree, une interruption nait `ignoree` : consulter
   *  l'heure n'est pas un reveil. C'est un defaut, pas une regle. */
  seuilReveilMinutes: number
  /** Ajoute au debut pour estimer l'endormissement. 0 = pas d'estimation. */
  decalageEndormissementMinutes: number
  preferenceFin: PreferenceFin
}

export const REGLAGES_DEFAUT: Reglages = {
  objectifMinutes: 8 * 60,
  seuilNuitMinutes: 90,
  seuilFusionMinutes: 20,
  seuilReveilMinutes: 5,
  decalageEndormissementMinutes: 0,
  preferenceFin: 'premier',
}

// ---------------------------------------------------------------------------
// Divers
// ---------------------------------------------------------------------------

export interface MetaEntry {
  cle: string
  valeur: unknown
}
